import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiEdit3, FiUsers, FiPieChart, FiPlayCircle, FiCamera } from 'react-icons/fi';
import { supabase, TABLES } from '../supabase/supabase';

const ProjectLayout = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [project, setProject] = useState(null);
  useEffect(() => {
    let active = true;
    supabase
      .from(TABLES.PROJECTS)
      .select('name, description')
      .eq('id', projectId)
      .maybeSingle()
      .then(({ data }) => { if (active) setProject(data); });
    return () => { active = false; };
  }, [projectId]);

  const projectName = project?.name || 'Loading…';

  // Hide sidebar if we are in an active session to give max space to the researcher
  const isActiveSession = location.pathname.includes('/active');

  if (isActiveSession) {
    return (
      <div className="h-screen w-full bg-gray-50 flex flex-col">
        <header className="h-14 bg-gray-900 text-white flex items-center px-4 justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/projects/${projectId}/sessions`)} className="p-2 hover:bg-gray-800 rounded-md transition-colors">
              <SafeIcon icon={FiArrowLeft} />
            </button>
            <span className="font-medium text-sm">Active Session • {projectName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
            <span className="text-xs font-medium text-gray-300 uppercase tracking-wider">Recording</span>
          </div>
        </header>
        <main className="flex-1 overflow-hidden relative">
          <Outlet />
        </main>
      </div>
    );
  }

  const navItems = [
    { path: 'setup', label: 'Project Setup', icon: FiEdit3 },
    { path: 'sessions', label: 'Sessions', icon: FiUsers },
    { path: 'analysis', label: 'Analysis', icon: FiPieChart },
    { path: 'snapshots', label: 'Snapshots', icon: FiCamera },
  ];

  return (
    <div className="flex h-screen w-full bg-gray-50 overflow-hidden font-sans text-gray-900">
      {/* Project Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col shadow-sm z-10">
        <div className="p-4 border-b border-gray-100">
          <button 
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-4"
          >
            <SafeIcon icon={FiArrowLeft} />
            <span>Back to Projects</span>
          </button>
          <h2 className="font-semibold text-lg truncate" title={projectName}>
            {projectName}
          </h2>
          <p className="text-xs text-gray-500 mt-1 truncate" title={project?.description || ''}>
            {project?.description || 'UX Research Study'}
          </p>
        </div>
        
        <nav className="p-4 space-y-1 flex-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={`/projects/${projectId}/${item.path}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                  isActive || location.pathname.includes(`/${item.path}`)
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              <SafeIcon icon={item.icon} className="text-lg" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Project Content Area */}
      <main className="flex-1 overflow-y-auto relative">
        <Outlet />
      </main>
    </div>
  );
};

export default ProjectLayout;